import { useState, useEffect } from 'react';
import { Bell, X, Sparkles } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useTranslation } from '../context/LanguageContext';

const SEEN_KEY = 'notifications_prompt_seen';

/**
 * Bottom sheet shown ONCE after the first login to encourage the user
 * to enable push notifications. Explains the VALUE before triggering
 * the OS-level permission prompt (Apple-recommended UX pattern).
 *
 * Flow:
 *   1. On mount, check if the user has already seen this sheet.
 *   2. If not, check if push permission is already granted/denied.
 *      - If already granted or explicitly denied → mark seen, hide.
 *      - Otherwise → show sheet.
 *   3. "Activate" → calls OneSignal requestPermission → marks seen.
 *   4. "Later" → marks seen (won't show again).
 */
export default function NotificationOptInSheet() {
  const { isDark } = useTheme();
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [requesting, setRequesting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const evaluate = async () => {
      try {
        if (localStorage.getItem(SEEN_KEY) === '1') return;

        // Check existing OS-level permission status. If already granted
        // or explicitly denied, don't bother the user.
        try {
          const { Capacitor } = await import('@capacitor/core');
          if (Capacitor.isNativePlatform()) {
            // Native: if we already have a OneSignal Player ID, the user
            // has already accepted (or was auto-subscribed).
            const { getOneSignalPlayerId } = await import('../lib/onesignal');
            const playerId = await getOneSignalPlayerId().catch(() => null);
            if (playerId) {
              localStorage.setItem(SEEN_KEY, '1');
              return;
            }
          } else if (typeof Notification !== 'undefined') {
            if (Notification.permission === 'granted' || Notification.permission === 'denied') {
              localStorage.setItem(SEEN_KEY, '1');
              return;
            }
          }
        } catch { /* fall through and show sheet */ }

        if (!cancelled) {
          // Small delay so it doesn't appear simultaneously with other
          // post-login banners (Premium welcome, support reply, etc.)
          setTimeout(() => !cancelled && setVisible(true), 1500);
        }
      } catch {
        /* silent */
      }
    };
    evaluate();
    return () => { cancelled = true; };
  }, []);

  const dismiss = (markSeen = true) => {
    if (markSeen) {
      try { localStorage.setItem(SEEN_KEY, '1'); } catch {}
    }
    setVisible(false);
  };

  // Lock background scroll while the sheet is visible — prevents the
  // Dashboard underneath from scrolling under the user's finger when
  // they interact with the popup.
  useEffect(() => {
    if (!visible) return;
    const prevOverflow = document.body.style.overflow;
    const prevPosition = document.body.style.position;
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.style.position = prevPosition;
      document.body.style.width = '';
    };
  }, [visible]);

  const activate = async () => {
    setRequesting(true);
    try {
      const {
        initOneSignal,
        requestOneSignalPermission,
        getOneSignalPlayerId,
        registerOneSignalPlayerOnBackend,
      } = await import('../lib/onesignal');
      await initOneSignal();
      const granted = await requestOneSignalPermission();
      if (granted) {
        try {
          const playerId = await getOneSignalPlayerId();
          if (playerId) await registerOneSignalPlayerOnBackend(playerId);
        } catch { /* non-fatal */ }
      }
    } catch (err) {
      console.warn('[NotificationOptIn] permission error:', err?.message || err);
    } finally {
      setRequesting(false);
      dismiss(true);
    }
  };

  if (!visible) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-end justify-center bg-black/60 backdrop-blur-sm animate-fade-in overscroll-contain touch-none"
      onClick={() => dismiss(true)}
      onTouchMove={e => e.preventDefault()}
      data-testid="notification-optin-sheet"
    >
      <div
        className={`w-full max-w-md rounded-t-3xl shadow-2xl animate-slide-up overflow-hidden ${
          isDark ? 'bg-[#1a1a1a]' : 'bg-white'
        }`}
        onClick={e => e.stopPropagation()}
      >
        {/* Drag handle */}
        <div className="flex justify-center pt-3 pb-1">
          <div className={`w-12 h-1.5 rounded-full ${isDark ? 'bg-white/20' : 'bg-gray-300'}`} />
        </div>

        {/* Hero icon */}
        <div className="px-6 pt-4 flex flex-col items-center">
          <div className="relative w-20 h-20 rounded-full bg-gradient-to-br from-[#E8334A] to-[#C41E3A] flex items-center justify-center shadow-xl">
            <Bell className="w-10 h-10 text-white" strokeWidth={2.2} />
            <div className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-amber-400 flex items-center justify-center border-2 border-white">
              <Sparkles className="w-3 h-3 text-white" strokeWidth={2.5} />
            </div>
          </div>

          <h3 className={`font-display text-2xl mt-5 text-center ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {t('notifOptIn.title') || 'Resta motivato ogni giorno'}
          </h3>
          <p className={`text-sm mt-2 text-center px-2 ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
            {t('notifOptIn.subtitle') || 'Attiva le notifiche per non perdere nulla del tuo percorso'}
          </p>
        </div>

        {/* Value props */}
        <ul className={`mt-5 px-6 space-y-2.5 text-sm ${isDark ? 'text-white/85' : 'text-gray-700'}`}>
          <li className="flex items-start gap-3">
            <span className="text-base">💪</span>
            <span>{t('notifOptIn.bullet1') || 'Promemoria per pesate, camminate e sfide'}</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="text-base">❤️</span>
            <span>{t('notifOptIn.bullet2') || 'Like, commenti e messaggi dei tuoi amici Slimmy'}</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="text-base">🏆</span>
            <span>{t('notifOptIn.bullet3') || 'Avvisi quando raggiungi un traguardo o vinci una sfida'}</span>
          </li>
        </ul>

        {/* CTA buttons */}
        <div className="px-5 pt-6 pb-6 space-y-2">
          <button
            type="button"
            data-testid="notif-optin-activate-btn"
            onClick={activate}
            disabled={requesting}
            className="w-full py-3.5 rounded-2xl font-semibold text-white bg-gradient-to-r from-[#E8334A] to-[#C41E3A] shadow-lg active:scale-[0.98] transition-transform disabled:opacity-70"
          >
            {requesting
              ? (t('notifOptIn.requesting') || 'Richiesta in corso...')
              : (t('notifOptIn.activate') || 'Attiva notifiche')}
          </button>
          <button
            type="button"
            data-testid="notif-optin-later-btn"
            onClick={() => dismiss(true)}
            className={`w-full py-3 rounded-2xl font-medium text-sm transition-colors ${
              isDark ? 'text-white/60 hover:text-white' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {t('notifOptIn.later') || 'Più tardi'}
          </button>
        </div>

        {/* Extra bottom padding so the CTA isn't covered by the
            persistent bottom tab bar (HOME/FEED/SFIDE/PROFILO). */}
        <div className="h-24" />
        <div className="pb-[env(safe-area-inset-bottom)]" />
      </div>
    </div>
  );
}
